import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Loads a jobsite by id from the existing V1 endpoint.
 * Returns { jobsite, legacyAreas, loading, error, reload }.
 *
 * `legacyAreas` are raw JobSite.areas records from the pre-Takeoff schema.
 * They exist when a jobsite has old Area documents not yet wrapped in a SiteTakeoff.
 */
export default function useJobsite(url, jobsiteId) {
  const [jobsite, setJobsite] = useState(null);
  const [legacyAreas, setLegacyAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!url || !jobsiteId) {
      setJobsite(null);
      setLegacyAreas([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${url}/api/customer/fetchJobSiteAreas?id=${jobsiteId}&includeAll=1`);
      const { success, jobSite: siteData, jobSiteAreas } = resp.data || {};
      if (success && siteData?._id) {
        setJobsite(siteData);
        setLegacyAreas(Array.isArray(jobSiteAreas) ? jobSiteAreas : []);
      } else {
        setJobsite(null);
        setLegacyAreas([]);
        setError('Jobsite not found');
      }
    } catch (err) {
      console.error('useJobsite load error:', err);
      setError(err.message || 'Failed to load jobsite');
      setJobsite(null);
      setLegacyAreas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [url, jobsiteId]);

  return { jobsite, legacyAreas, loading, error, reload: load };
}

/**
 * Extract lat/lng from a jobsite doc. Returns [lat, lng] or null.
 * [0, 0] is the "geocode failed" sentinel, not a real location.
 */
export function getJobsiteCoords(jobsite) {
  if (!jobsite) return null;
  if (jobsite.location?.coordinates?.length === 2) {
    const [lng, lat] = jobsite.location.coordinates;
    if (lat !== 0 || lng !== 0) return [lat, lng];
  }
  if (jobsite.latitude && jobsite.longitude) {
    return [jobsite.latitude, jobsite.longitude];
  }
  return null;
}
